import { useLocation, useNavigate } from "react-router-dom";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import Loader from "../../../../../components/loader";

const PRIORITY_STYLES = {
  P0: "bg-green-100 text-green-800 border border-green-200",
  P1: "bg-yellow-100 text-yellow-800 border border-yellow-200",
  P2: "bg-orange-100 text-orange-800 border border-orange-200",
  P3: "bg-red-100 text-red-800 border border-red-200",
  default: "bg-gray-100 text-gray-800 border border-gray-200",
};

export default function ({ notion: initialNotion }) {
  const navigate = useNavigate();
  const { search } = useLocation();
  const query = new URLSearchParams(search);
  const user = useSelector((state) => state.Auth.user);

  const [notion, setNotion] = useState(initialNotion);

  useEffect(() => {
    setNotion(initialNotion);
  }, [initialNotion]);

  const CategoryBadge = () => {
    if (!notion?.category) return null;
    const colors = {
      FEATURE: "bg-blue-500 text-white",
      BUG: "bg-red-500 text-white",
    };

    return <div className={`px-2 py-1 rounded-sm text-xs font-medium ${colors[notion.category]}`}>{notion.category}</div>;
  };

  const NotionTags = () => {
    if (!notion?.tags) return null;
    return (
      <div className="flex flex-wrap gap-1 text-xs">
        {notion?.tags.map((tag) => (
          <span key={tag} className="text-xs w-fit text-gray-600 bg-gray-100 px-2 py-1 rounded border border-gray-200">
            {tag}
          </span>
        ))}
      </div>
    );
  };

  if (!user) return <Loader />;

  return (
    <div className="w-[350px]">
      <div
        className="relative group p-3 mb-1.5 bg-white rounded-lg border border-gray-200 shadow-sm transition-all duration-200 cursor-pointer hover:border-gray-300"
        onClick={() => {
          query.set("task_modal_id", notion._id);
          navigate({ search: query.toString() });
        }}>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center space-x-1.5">
            <span className="text-gray-400 font-mono text-xs">#{notion?.index}</span>
            {notion?.priority && (
              <span className={`px-1.5 py-0.5 rounded-sm text-xs font-medium ${PRIORITY_STYLES[notion.priority] || PRIORITY_STYLES.default}`}>{notion.priority}</span>
            )}
            {notion?.category && <CategoryBadge />}
          </div>
          <div className="flex items-center gap-0.5 text-xs text-gray-500">
            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
            </svg>
            <span>{daysAgo(notion?.created_at)}</span>
          </div>
        </div>

        <h3 className="text-sm font-medium text-gray-900 mb-1.5 group-hover:text-blue-600 transition-colors duration-200 whitespace-normal">{notion?.name}</h3>

        {notion?.tags?.length > 0 && (
          <div className="mb-2 flex flex-wrap gap-1">
            <NotionTags />
          </div>
        )}

        <div className="space-y-1.5 text-xs">
          {notion.budget_name && (
            <div className="flex items-center text-xs text-gray-600 bg-gray-50 rounded-md px-1.5 py-1">
              <svg className="w-3.5 h-3.5 mr-1 text-purple-500" fill="currentColor" viewBox="0 0 20 20">
                <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z" />
                <path
                  fillRule="evenodd"
                  d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z"
                  clipRule="evenodd"
                />
              </svg>
              {notion.budget_name}
            </div>
          )}

          {notion?.effort_tshirt_size && (
            <div className="flex items-center text-xs text-gray-600 bg-gray-50 rounded-md px-1.5 py-1">
              <svg className="w-3.5 h-3.5 mr-1 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              <span>Effort: </span>
              <span className="ml-1 font-medium">{notion.effort_tshirt_size}</span>
            </div>
          )}

          {notion?.epic_module && (
            <div className="flex items-center text-xs text-gray-600 bg-gray-50 rounded-md px-1.5 py-1">
              <svg className="w-3.5 h-3.5 mr-1 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zm0 6a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1v-2zm0 6a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1v-2z" clipRule="evenodd" />
              </svg>
              <span>Module: </span>
              <span className="ml-1 font-medium">{notion.epic_module}</span>
            </div>
          )}
        </div>

        {notion.users?.length > 0 && (
          <div className="flex items-center justify-between mt-1.5 pt-1.5">
            <div className="flex gap-1.5">
              {notion.users.map((user) => (
                <div key={user.user_avatar} className="relative flex items-center gap-0.5">
                  <img className="w-5 h-5 rounded-full border-2 border-white ring-1 ring-transparent transition-all duration-200" src={user.user_avatar} alt={user.user_name} />
                  <span className="text-xs text-gray-500">{user.user_name?.split(' ').map(n => n[0]).join('')}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="absolute inset-0 rounded-lg border border-blue-500 opacity-0 hover:opacity-100 transition-opacity duration-200 pointer-events-none" />
      </div>
    </div>
  );
}

const daysAgo = (date) => {
  const today = new Date();
  const diff = today - new Date(date);
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  return days === 0 ? "Today" : `${days}d ago`;
};
